import { downloadXlsx, type XlsxCell } from '@/shared/lib/xlsx';
import { tituloColumna } from './EstadoFinancieroReport';
import { MOSTRAR_CAUCION } from './api';
import type { EfResponse, EvolucionAgrupar, EvolucionResponse, FlujoMesRow } from './api';

/**
 * Exportación a Excel de las tres solapas del tablero.
 *
 * ponytail: estos armadores NO calculan nada — recorren exactamente los
 * mismos arrays que pinta la tabla (`data.rows`, `data.meses`,
 * `flujoPorMes.rows`) y con los mismos accesores. Es a propósito: si el
 * archivo re-agregara por su cuenta podría diferir de la pantalla, que es
 * justo el tipo de bug que nadie nota hasta que alguien manda el Excel a un
 * tercero. Misma regla que sigue `buildFlujoPorMes` en el backend.
 *
 * Los importes viajan como NÚMEROS (no strings con `$`) para que sumen en
 * Excel; los ratios como porcentaje ya multiplicado (4,7 y no 0,047), que es
 * como se leen en pantalla.
 */

/** `09/2025` → `09-2025`, que sí es un nombre de archivo válido. */
function periodoSlug(desde: string, hasta: string): string {
  return `${desde.replace('/', '-')}_${hasta.replace('/', '-')}`;
}

const pct = (v: number | null): XlsxCell =>
  v === null ? '' : Math.round(v * 1000) / 10;

export async function exportFlujoPorMes(data: EfResponse): Promise<void> {
  const hayCaucion = MOSTRAR_CAUCION && data.caucion !== null;
  const headers = [
    data.granularidad === 'diario' ? 'Fecha' : 'Mes-Año',
    'Ingresos',
    'Egresos',
    'G/P operativo',
    'Comisiones / Ventas %',
    'Costo dir. / Ingresos %',
    'Costo ind. / Ingresos %',
    'Costo total / Ingresos %',
    ...(hayCaucion ? [`Caución (${data.caucion?.pct}% cash, ${data.moneda})`] : []),
  ];

  const fila = (r: FlujoMesRow, etiqueta: string): XlsxCell[] => [
    etiqueta,
    r.ingresos,
    // Negativo, igual que en pantalla: el archivo tiene que leerse igual que
    // la tabla de la que salió.
    -r.egresos,
    r.gpOperativo,
    pct(r.comisionesSobreVentas),
    pct(r.costoDirectoSobreIngresos),
    pct(r.costoIndirectoSobreIngresos),
    pct(r.costoTotalSobreIngresos),
    ...(hayCaucion ? [r.caucion] : []),
  ];

  await downloadXlsx({
    filename: `Flujo-por-mes_${periodoSlug(data.desde, data.hasta)}`,
    sheetName: 'Flujo por mes',
    headers,
    rows: [
      ...data.flujoPorMes.rows.map((r) => fila(r, tituloColumna(r.mes, data.granularidad))),
      fila(data.flujoPorMes.total, 'Total'),
      ...data.flujoPorMes.proyectadas.map((r) => fila(r, `Proy. ${r.mes}`)),
    ],
    totals: notasDelPeriodo(data),
  });
}

export async function exportEstadoFinanciero(data: EfResponse): Promise<void> {
  const headers = [
    'Ítem',
    ...data.meses.map((m) => tituloColumna(m, data.granularidad)),
    'Total',
  ];

  const rows: XlsxCell[][] = data.rows.map((r) => [
    // Mismo sangrado que la tabla: sin esto se pierde qué SubÍtem cuelga de
    // qué Ítem, que es toda la estructura del estado.
    r.kind === 'subitem' ? `   · ${r.label}` : r.label,
    ...data.meses.map((m) => r.porMes[m] ?? 0),
    r.total,
  ]);

  if (MOSTRAR_CAUCION && data.caucion) {
    const c = data.caucion;
    rows.push([
      `Caución (${c.pct}% cash, ${data.moneda})`,
      ...data.meses.map((m) => c.porMes[m] ?? 0),
      data.meses.reduce((acc, m) => acc + (c.porMes[m] ?? 0), 0),
    ]);
  }

  await downloadXlsx({
    filename: `Estado-Financiero_${periodoSlug(data.desde, data.hasta)}`,
    sheetName: 'Estado Financiero',
    headers,
    rows,
    totals: notasDelPeriodo(data),
  });
}

const EJE_LABEL: Record<EvolucionAgrupar, string> = {
  categoria: 'Categoría',
  cuenta: 'Cuenta contable',
  subitem: 'SubÍtem EF',
};

export async function exportEvolucion(
  evo: EvolucionResponse,
  desde: string,
  hasta: string,
): Promise<void> {
  const proyMeses = evo.proyeccion.meses;
  const headers = [
    EJE_LABEL[evo.agrupar],
    ...evo.meses.map((m) => tituloColumna(m, evo.granularidad)),
    'Total real',
    ...proyMeses.map((m) => `Proy. ${m}`),
    ...(proyMeses.length > 0 ? ['Total + Proyectado'] : []),
  ];

  const proyDe = (label: string, mes: string) => evo.proyeccion.porLabel[label]?.[mes] ?? 0;
  const filaProy = (label: string, total: number): XlsxCell[] => {
    const proys = proyMeses.map((m) => proyDe(label, m));
    return [
      ...proys,
      ...(proyMeses.length > 0 ? [total + proys.reduce((a, b) => a + b, 0)] : []),
    ];
  };

  const rows: XlsxCell[][] = evo.rows.map((r) => [
    r.label,
    ...evo.meses.map((m) => r.porMes[m] ?? 0),
    r.total,
    ...filaProy(r.label, r.total),
  ]);

  // Total: los proyectados se suman columna a columna sobre todas las filas,
  // igual que la fila Total de la pantalla.
  const totalProy = proyMeses.map((m) =>
    Object.values(evo.proyeccion.porLabel).reduce((acc, porMes) => acc + (porMes[m] ?? 0), 0),
  );
  rows.push([
    'Total',
    ...evo.meses.map((m) => evo.totalPorMes[m] ?? 0),
    evo.total,
    ...totalProy,
    ...(proyMeses.length > 0
      ? [evo.total + totalProy.reduce((a, b) => a + b, 0)]
      : []),
  ]);

  await downloadXlsx({
    filename: `Evolucion-${evo.agrupar}_${periodoSlug(desde, hasta)}`,
    sheetName: 'Evolución',
    headers,
    rows,
    totals: [
      { label: 'Moneda', value: evo.moneda },
      { label: 'Período', value: `${desde} → ${hasta}` },
      { label: 'Agrupado por', value: EJE_LABEL[evo.agrupar] },
    ],
  });
}

/**
 * Pie del archivo. Lleva las salvedades que en pantalla están en el panel de
 * avisos — un Excel se reenvía suelto y sin esto viaja sin contexto.
 */
function notasDelPeriodo(data: EfResponse): Array<{ label: string; value: string | number }> {
  const notas = [
    { label: 'Moneda', value: data.moneda },
    { label: 'Período', value: `${data.desde} → ${data.hasta}` },
  ];
  if (data.sinConfirmar.movimientos > 0) {
    notas.push({
      label: 'Períodos provisorios',
      value: `${data.sinConfirmar.meses.join(', ')} — ${data.sinConfirmar.movimientos} movimientos sin Cierre de Caja`,
    });
  }
  if (MOSTRAR_CAUCION && data.caucion && data.caucion.extrapolado.meses > 0) {
    notas.push({
      label: 'Caución estimada',
      value: `${data.caucion.extrapolado.meses} períodos con la tasa del ${data.caucion.ultimaTasa} arrastrada`,
    });
  }
  notas.push({
    label: 'Cotización',
    value: `serie diaria "${data.casa}", por fecha de cada movimiento`,
  });
  if (data.sinClasificar.movimientos > 0) {
    notas.push({
      label: 'Sin clasificar',
      value: `${data.sinClasificar.movimientos} movimientos sin SubÍtem de EF`,
    });
  }
  return notas;
}
